/**
 * Read the cell text out of an .xlsx workbook.
 *
 * Only what an import needs: every sheet's name and its cells as strings.
 * Shared strings, inline strings, rich text, numbers, booleans and dates
 * (by number format, written as YYYY-MM-DD or YYYY-MM-DD HH:mm) are handled.
 * Hyperlinked cells whose text isn't itself a link (a cell reading "profile"
 * that links to tiktok.com/@name) yield the link instead.
 *
 * Formulas give their cached value. Styling, merged cells and charts are
 * ignored.
 */
import { readZipDirectory, readZipText, ZipError, type ZipEntry } from './zip';

export interface SheetData {
  name: string;
  rows: string[][];
}

export class SpreadsheetError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SpreadsheetError';
  }
}

/** Hard ceiling so a stray million-row sheet can't lock up the tab. */
export const MAX_SHEET_ROWS = 50_000;

const NS_REL = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';

function parseXml(text: string): Document {
  const doc = new DOMParser().parseFromString(text, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new SpreadsheetError('The spreadsheet is damaged and could not be read.');
  }
  return doc;
}

function all(parent: Document | Element, localName: string): Element[] {
  return Array.from(parent.getElementsByTagNameNS('*', localName));
}

function children(parent: Element, localName: string): Element[] {
  const out: Element[] = [];
  for (let c = parent.firstElementChild; c; c = c.nextElementSibling) {
    if (c.localName === localName) out.push(c);
  }
  return out;
}

/** Text of a <si> or <is>: every <t>, skipping phonetic runs (<rPh>). */
function richText(el: Element): string {
  let out = '';
  for (const t of all(el, 't')) {
    if (t.parentElement?.localName === 'rPh') continue;
    out += t.textContent ?? '';
  }
  return out;
}

/** "AB12" → 27 (zero-based column). */
export function columnIndex(ref: string): number {
  let col = 0;
  for (const ch of ref.toUpperCase()) {
    const code = ch.charCodeAt(0);
    if (code < 65 || code > 90) break;
    col = col * 26 + (code - 64);
  }
  return col - 1;
}

function rowIndex(ref: string): number {
  const m = /(\d+)$/.exec(ref);
  return m ? Number.parseInt(m[1]!, 10) - 1 : -1;
}

/** Path of a relationship target, resolved against the part that owns it. */
function resolveTarget(base: string, target: string): string {
  if (target.startsWith('/')) return target.slice(1);
  const parts = base.split('/').slice(0, -1);
  for (const seg of target.split('/')) {
    if (seg === '..') parts.pop();
    else if (seg !== '.') parts.push(seg);
  }
  return parts.join('/');
}

function relsPathFor(part: string): string {
  const i = part.lastIndexOf('/');
  return `${part.slice(0, i + 1)}_rels/${part.slice(i + 1)}.rels`;
}

async function readRels(bytes: Uint8Array, entries: Map<string, ZipEntry>, part: string): Promise<Map<string, string>> {
  const text = await readZipText(bytes, entries, relsPathFor(part));
  const out = new Map<string, string>();
  if (!text) return out;
  for (const rel of all(parseXml(text), 'Relationship')) {
    const id = rel.getAttribute('Id');
    const target = rel.getAttribute('Target');
    if (!id || target === null) continue;
    const external = rel.getAttribute('TargetMode') === 'External';
    out.set(id, external ? target : resolveTarget(part, target));
  }
  return out;
}

// ── Dates ─────────────────────────────────────────────────────────────────

const BUILTIN_DATE_FORMATS = new Set([14, 15, 16, 17, 18, 19, 20, 21, 22, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 45, 46, 47, 50, 51, 52, 53, 54, 55, 56, 57, 58]);

export function isDateFormatCode(code: string): boolean {
  const stripped = code
    .replace(/"[^"]*"/g, '')
    .replace(/\\./g, '')
    .replace(/\[[^\]]*\]/g, '')
    .replace(/_./g, '')
    .replace(/\*./g, '');
  return /[dmyhs]/i.test(stripped) && !/^[#0.,%\s]*$/.test(stripped);
}

const pad = (n: number): string => String(n).padStart(2, '0');

/** Excel serial date → "YYYY-MM-DD" (or "YYYY-MM-DD HH:mm" when it has a time). */
export function excelSerialToText(serial: number, date1904 = false): string {
  const days = serial + (date1904 ? 1462 : 0) - 25569;
  const ms = Math.round(days * 86_400_000);
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return String(serial);
  const date = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
  const hasTime = Math.abs(serial - Math.trunc(serial)) > 1e-9;
  return hasTime ? `${date} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}` : date;
}

function numberText(v: string): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  // Keep integers integral; trim float noise like 0.30000000000000004.
  return Number.isInteger(n) ? String(n) : String(Number.parseFloat(n.toPrecision(15)));
}

// ── Workbook ──────────────────────────────────────────────────────────────

interface Styles {
  dateStyles: Set<number>;
}

function readStyles(text: string | null): Styles {
  const dateStyles = new Set<number>();
  if (!text) return { dateStyles };
  const doc = parseXml(text);
  const custom = new Map<number, string>();
  for (const f of all(doc, 'numFmt')) {
    custom.set(Number(f.getAttribute('numFmtId')), f.getAttribute('formatCode') ?? '');
  }
  const cellXfs = all(doc, 'cellXfs')[0];
  if (cellXfs) {
    children(cellXfs, 'xf').forEach((xf, i) => {
      const id = Number(xf.getAttribute('numFmtId') ?? '0');
      const code = custom.get(id);
      if (BUILTIN_DATE_FORMATS.has(id) || (code !== undefined && isDateFormatCode(code))) dateStyles.add(i);
    });
  }
  return { dateStyles };
}

function looksLikeLink(text: string): boolean {
  return /^(https?:\/\/|www\.)/i.test(text.trim()) || /\.(com|am|be|gg)\//i.test(text);
}

function readSheet(
  doc: Document,
  shared: string[],
  styles: Styles,
  date1904: boolean,
  links: Map<string, string>,
): string[][] {
  const rows: string[][] = [];

  const sheetData = all(doc, 'sheetData')[0];
  if (!sheetData) return rows;
  let nextRow = 0;
  for (const rowEl of children(sheetData, 'row')) {
    const r = rowEl.getAttribute('r');
    const ri = r ? Number.parseInt(r, 10) - 1 : nextRow;
    nextRow = ri + 1;
    if (ri >= MAX_SHEET_ROWS) {
      throw new SpreadsheetError(`This sheet has more than ${MAX_SHEET_ROWS.toLocaleString('en-US')} rows. Split it into smaller files.`);
    }
    const cells: string[] = [];
    let nextCol = 0;
    for (const c of children(rowEl, 'c')) {
      const ref = c.getAttribute('r');
      const ci = ref ? columnIndex(ref) : nextCol;
      nextCol = ci + 1;
      const type = c.getAttribute('t') ?? 'n';
      const v = children(c, 'v')[0]?.textContent ?? '';
      let text: string;
      switch (type) {
        case 's':
          text = shared[Number.parseInt(v, 10)] ?? '';
          break;
        case 'inlineStr': {
          const is = children(c, 'is')[0];
          text = is ? richText(is) : '';
          break;
        }
        case 'b':
          text = v === '1' ? 'TRUE' : v === '0' ? 'FALSE' : v;
          break;
        case 'e':
          text = '';
          break;
        case 'str':
        case 'd':
          text = v;
          break;
        default: {
          const s = Number(c.getAttribute('s') ?? '0');
          text = v !== '' && styles.dateStyles.has(s) && Number.isFinite(Number(v)) ? excelSerialToText(Number(v), date1904) : numberText(v);
        }
      }
      if (ref && links.has(ref.toUpperCase()) && !looksLikeLink(text)) {
        text = links.get(ref.toUpperCase()) ?? text;
      }
      cells[ci] = text;
    }
    for (let i = 0; i < cells.length; i++) if (cells[i] === undefined) cells[i] = '';
    rows[ri] = cells;
  }
  // Fill gaps (rows the file skipped) and drop trailing blank rows.
  for (let i = 0; i < rows.length; i++) if (!rows[i]) rows[i] = [];
  while (rows.length > 0 && rows[rows.length - 1]!.every((c) => c.trim() === '')) rows.pop();
  return rows;
}

/** Cell → hyperlink target, from a sheet's <hyperlinks> and its rels. */
function readHyperlinks(doc: Document, rels: Map<string, string>): Map<string, string> {
  const out = new Map<string, string>();
  if (rels.size === 0) return out;
  for (const h of all(doc, 'hyperlink')) {
    const ref = h.getAttribute('ref');
    const id = h.getAttributeNS(NS_REL, 'id') ?? h.getAttribute('r:id');
    const target = id ? rels.get(id) : undefined;
    if (!ref || !target) continue;
    const [from, to] = ref.toUpperCase().split(':');
    if (!to) {
      out.set(from!, target);
      continue;
    }
    // A range: expand when it is one column (the common case).
    const col = from!.replace(/\d+$/, '');
    if (col !== to.replace(/\d+$/, '')) {
      out.set(from!, target);
      continue;
    }
    const a = rowIndex(from!);
    const b = rowIndex(to);
    for (let r = a; r <= b && r - a < MAX_SHEET_ROWS; r++) out.set(`${col}${r + 1}`, target);
  }
  return out;
}

/** Every sheet in the workbook, in tab order. */
export async function readXlsx(bytes: Uint8Array): Promise<SheetData[]> {
  let entries: Map<string, ZipEntry>;
  try {
    entries = readZipDirectory(bytes);
  } catch (err) {
    if (err instanceof ZipError) throw new SpreadsheetError("This file isn't a valid .xlsx workbook.");
    throw err;
  }
  const workbookPath = 'xl/workbook.xml';
  const workbookXml = await readZipText(bytes, entries, workbookPath);
  if (!workbookXml) throw new SpreadsheetError("This file isn't a valid .xlsx workbook.");
  const workbook = parseXml(workbookXml);
  const wbPr = all(workbook, 'workbookPr')[0];
  const date1904 = ['1', 'true'].includes(wbPr?.getAttribute('date1904') ?? '');
  const wbRels = await readRels(bytes, entries, workbookPath);

  const sharedXml = await readZipText(bytes, entries, 'xl/sharedStrings.xml');
  const shared = sharedXml ? all(parseXml(sharedXml), 'si').map(richText) : [];
  const styles = readStyles(await readZipText(bytes, entries, 'xl/styles.xml'));

  const out: SheetData[] = [];
  for (const sheet of all(workbook, 'sheet')) {
    const name = sheet.getAttribute('name') ?? `Sheet ${out.length + 1}`;
    if (sheet.getAttribute('state') === 'hidden' || sheet.getAttribute('state') === 'veryHidden') continue;
    const rid = sheet.getAttributeNS(NS_REL, 'id') ?? sheet.getAttribute('r:id');
    const path = (rid && wbRels.get(rid)) || `xl/worksheets/sheet${out.length + 1}.xml`;
    const xml = await readZipText(bytes, entries, path);
    if (xml === null) continue; // chart sheets and the like
    const doc = parseXml(xml);
    const links = readHyperlinks(doc, await readRels(bytes, entries, path));
    out.push({ name, rows: readSheet(doc, shared, styles, date1904, links) });
  }
  if (out.length === 0) throw new SpreadsheetError('This workbook has no sheets with data.');
  return out;
}
